import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';
import { PLATFORM_PRIMARY, PLATFORM_SECONDARY } from './brand';

const displayFontLtr =
  '"Outfit", "Plus Jakarta Sans", "Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const uiFontLtr =
  '"Plus Jakarta Sans", "Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const fontFamilyRtl =
  '"Noto Sans Arabic", "Cairo", "Tajawal", "Segoe UI", "Tahoma", "Arial", sans-serif';

export function createAppTheme(
  mode: 'light' | 'dark',
  primaryColor = PLATFORM_PRIMARY,
  secondaryColor = PLATFORM_SECONDARY,
  direction: 'ltr' | 'rtl' = 'ltr',
) {
  const isLight = mode === 'light';
  const isRtl = direction === 'rtl';
  const displayFont = isRtl ? fontFamilyRtl : displayFontLtr;
  const fontFamily = isRtl ? fontFamilyRtl : uiFontLtr;
  const tracking = isRtl ? 0 : undefined;

  const safePrimary = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(String(primaryColor || ''))
    ? String(primaryColor)
    : PLATFORM_PRIMARY;
  const safeSecondary = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(String(secondaryColor || ''))
    ? String(secondaryColor)
    : PLATFORM_SECONDARY;

  const ink = isLight ? '#0B1220' : '#F3F6FB';
  const muted = isLight ? '#5B6578' : '#9AA6B8';
  const hairline = isLight ? alpha('#0B1220', 0.08) : alpha('#E8EEF8', 0.1);
  const surface = isLight ? '#FFFFFF' : '#121A2B';
  const canvas = isLight ? '#F4F6FA' : '#090D16';

  const heading = {
    fontFamily: displayFont,
    letterSpacing: tracking ?? '-0.03em',
  };

  const options = {
    direction,
    palette: {
      mode,
      primary: {
        main: safePrimary,
        light: alpha(safePrimary, 0.12),
        dark: isLight ? '#102A45' : safePrimary,
        contrastText: '#ffffff',
      },
      secondary: {
        main: safeSecondary,
        light: alpha(safeSecondary, 0.12),
        dark: isLight ? '#6d28d9' : safeSecondary,
        contrastText: '#ffffff',
      },
      success: { main: '#059669', light: alpha('#059669', 0.12) },
      warning: { main: '#d97706', light: alpha('#d97706', 0.12) },
      error: { main: '#dc2626', light: alpha('#dc2626', 0.12), dark: '#7f1d1d' },
      info: { main: '#0284c7', light: alpha('#0284c7', 0.12) },
      background: {
        default: canvas,
        paper: surface,
      },
      text: {
        primary: ink,
        secondary: muted,
      },
      divider: hairline,
    },
    typography: {
      fontFamily,
      fontSize: 14,
      htmlFontSize: 16,
      h1: { ...heading, fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.12, letterSpacing: tracking ?? '-0.04em' },
      h2: { ...heading, fontWeight: 700, fontSize: '2rem', lineHeight: 1.16, letterSpacing: tracking ?? '-0.035em' },
      h3: { ...heading, fontWeight: 700, fontSize: '1.625rem', lineHeight: 1.2, letterSpacing: tracking ?? '-0.03em' },
      h4: { ...heading, fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.25, letterSpacing: tracking ?? '-0.025em' },
      h5: { ...heading, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.3, letterSpacing: tracking ?? '-0.02em' },
      h6: { ...heading, fontWeight: 600, fontSize: '1rem', lineHeight: 1.35, letterSpacing: tracking ?? '-0.015em' },
      subtitle1: { fontFamily: displayFont, fontWeight: 600, letterSpacing: tracking ?? '-0.01em', lineHeight: 1.45 },
      subtitle2: { fontFamily: displayFont, fontWeight: 600, fontSize: '0.8125rem', letterSpacing: tracking ?? '-0.008em' },
      body1: { lineHeight: 1.65, fontSize: '0.9375rem', fontWeight: 400 },
      body2: { lineHeight: 1.6, fontSize: '0.875rem', fontWeight: 400 },
      button: {
        fontFamily: displayFont,
        fontWeight: 600,
        letterSpacing: tracking ?? '0.01em',
        textTransform: 'none' as const,
      },
      overline: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: isRtl ? 0 : '0.12em',
        fontSize: '0.68rem',
      },
      caption: { fontWeight: 500, letterSpacing: tracking ?? '0.01em', lineHeight: 1.45 },
    },
    shape: { borderRadius: 14 },
    shadows: [
      'none',
      '0 1px 2px rgba(11,18,32,0.04)',
      '0 2px 6px rgba(11,18,32,0.05)',
      '0 4px 12px rgba(11,18,32,0.06)',
      '0 8px 20px rgba(11,18,32,0.07)',
      '0 12px 28px rgba(11,18,32,0.08)',
      '0 16px 36px rgba(11,18,32,0.09)',
      '0 20px 44px rgba(11,18,32,0.1)',
      '0 24px 52px rgba(11,18,32,0.11)',
      '0 28px 60px rgba(11,18,32,0.12)',
      '0 32px 68px rgba(11,18,32,0.13)',
      '0 36px 76px rgba(11,18,32,0.14)',
      '0 40px 84px rgba(11,18,32,0.15)',
      '0 44px 92px rgba(11,18,32,0.16)',
      '0 48px 100px rgba(11,18,32,0.17)',
      '0 52px 108px rgba(11,18,32,0.18)',
      '0 56px 116px rgba(11,18,32,0.19)',
      '0 60px 124px rgba(11,18,32,0.2)',
      '0 64px 132px rgba(11,18,32,0.21)',
      '0 68px 140px rgba(11,18,32,0.22)',
      '0 72px 148px rgba(11,18,32,0.23)',
      '0 76px 156px rgba(11,18,32,0.24)',
      '0 80px 164px rgba(11,18,32,0.25)',
      '0 84px 172px rgba(11,18,32,0.26)',
      '0 88px 180px rgba(11,18,32,0.27)',
    ] as ThemeOptions['shadows'],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { direction, scrollBehavior: 'smooth' },
          body: {
            direction,
            fontFamily,
            backgroundColor: canvas,
            backgroundImage: isLight
              ? `radial-gradient(1200px 480px at 88% -8%, ${alpha(safePrimary, 0.07)} 0%, transparent 55%)`
              : `radial-gradient(1000px 420px at 90% -10%, ${alpha(safePrimary, 0.14)} 0%, transparent 50%)`,
            ...(isRtl ? { letterSpacing: 0, wordSpacing: '0.02em', textAlign: 'start' as const } : {}),
          },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-track': { background: 'transparent' },
          '::-webkit-scrollbar-thumb': {
            background: isLight ? alpha('#64748b', 0.28) : alpha('#94a3b8', 0.28),
            borderRadius: 999,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '.MuiDataGrid-root': {
            border: `1px solid ${hairline} !important`,
            borderRadius: '14px !important',
            overflow: 'hidden',
            backgroundColor: `${surface} !important`,
            fontFamily,
          },
          '.MuiDataGrid-columnHeaders, .MuiDataGrid-columnHeader': {
            backgroundColor: `${isLight ? '#F3F5F8' : alpha('#0B1220', 0.6)} !important`,
          },
          '.MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${hairline} !important`,
            minHeight: '48px !important',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            fontFamily: `${displayFont} !important`,
            fontWeight: '600 !important',
            fontSize: '0.68rem !important',
            letterSpacing: '0.08em !important',
            textTransform: 'uppercase',
            color: `${muted} !important`,
          },
          '.MuiDataGrid-row:hover': {
            backgroundColor: `${alpha(safePrimary, isLight ? 0.045 : 0.1)} !important`,
          },
          '.MuiDataGrid-cell': {
            borderBottom: `1px solid ${isLight ? alpha('#0B1220', 0.05) : alpha('#E8EEF8', 0.07)} !important`,
            fontSize: '0.8375rem !important',
            color: ink,
          },
          '.MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${hairline} !important`,
            backgroundColor: `${isLight ? '#F8F9FB' : alpha('#0B1220', 0.4)} !important`,
            minHeight: '52px !important',
          },
          '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },
          '.MuiTableHead .MuiTableCell-head': {
            fontFamily: displayFont,
            fontWeight: 600,
            fontSize: '0.68rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: muted,
            backgroundColor: isLight ? '#F3F5F8' : alpha('#0B1220', 0.55),
          },
        },
      },
      MuiButton: {
        defaultProps: { color: 'primary', disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 10,
            paddingInline: 16,
            paddingBlock: 8,
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            letterSpacing: '-0.01em',
            transition: 'background-color 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
            '&:hover': { boxShadow: 'none' },
          },
          contained: {
            backgroundColor: `${safePrimary} !important`,
            color: '#ffffff !important',
            '&:hover': {
              backgroundColor: `${isLight ? '#102A45' : alpha(safePrimary, 0.88)} !important`,
              boxShadow: `0 8px 20px ${alpha(safePrimary, 0.24)}`,
            },
            '&.Mui-disabled': {
              backgroundColor: `${isLight ? alpha('#0B1220', 0.12) : alpha('#E8EEF8', 0.16)} !important`,
              color: `${isLight ? alpha('#0B1220', 0.38) : alpha('#E8EEF8', 0.4)} !important`,
              boxShadow: 'none !important',
            },
          },
          containedError: {
            backgroundColor: '#B42318 !important',
            '&:hover': { backgroundColor: '#912018 !important' },
          },
          containedWarning: {
            backgroundColor: '#B54708 !important',
            '&:hover': { backgroundColor: '#93370D !important' },
          },
          containedInherit: {
            backgroundColor: '#ffffff !important',
            color: `${safePrimary} !important`,
            '&:hover': { backgroundColor: '#F4F6FA !important' },
          },
          outlined: {
            borderColor: `${alpha(safePrimary, 0.28)} !important`,
            color: `${safePrimary} !important`,
            '&:hover': {
              borderColor: `${safePrimary} !important`,
              bgcolor: alpha(safePrimary, 0.05),
            },
          },
          outlinedError: {
            borderColor: `${alpha('#B42318', 0.4)} !important`,
            color: '#B42318 !important',
          },
          text: {
            color: `${safePrimary} !important`,
            '&:hover': { bgcolor: alpha(safePrimary, 0.06) },
          },
          textError: { color: '#B42318 !important' },
          sizeLarge: { paddingInline: 24, paddingBlock: 11, fontSize: '0.9375rem', borderRadius: 11 },
          sizeSmall: { paddingInline: 12, paddingBlock: 5, fontSize: '0.8125rem', borderRadius: 8 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: alpha(safePrimary, 0.08) },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${hairline}`,
          },
          elevation0: { boxShadow: 'none' },
          elevation1: {
            boxShadow: isLight ? '0 1px 2px rgba(11,18,32,0.04)' : '0 1px 3px rgba(0,0,0,0.28)',
          },
          elevation2: {
            boxShadow: isLight ? '0 8px 24px rgba(11,18,32,0.06)' : '0 8px 24px rgba(0,0,0,0.35)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: 16,
            border: `1px solid ${hairline}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8, fontSize: '0.75rem', fontFamily: displayFont },
          sizeSmall: { borderRadius: 7, fontSize: '0.7rem' },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined', fullWidth: true, margin: 'none' },
      },
      MuiFormControl: {
        defaultProps: { size: 'small', fullWidth: true, margin: 'none' },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: '0.875rem',
            color: muted,
            '&.MuiInputLabel-shrink': { fontSize: '0.8rem', fontWeight: 700 },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isLight ? '#F7F8FB' : alpha('#0B1220', 0.45),
            transition: 'box-shadow 0.18s, border-color 0.18s, background-color 0.18s',
            minHeight: 42,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? alpha('#0B1220', 0.08) : alpha('#E8EEF8', 0.12),
              borderWidth: 1,
            },
            '&.Mui-disabled': {
              backgroundColor: isLight ? alpha('#F4F6FA', 0.9) : alpha('#334155', 0.28),
            },
            '&:hover': {
              backgroundColor: isLight ? '#FFFFFF' : alpha('#0B1220', 0.55),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(safePrimary, 0.35),
            },
            '&.Mui-focused': {
              backgroundColor: isLight ? '#FFFFFF' : alpha('#0B1220', 0.6),
              boxShadow: `0 0 0 3px ${alpha(safePrimary, isLight ? 0.12 : 0.22)}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: safePrimary,
              borderWidth: 1.5,
            },
            '&.Mui-error': { boxShadow: `0 0 0 3px ${alpha('#dc2626', isLight ? 0.1 : 0.2)}` },
          },
          input: {
            paddingTop: 9,
            paddingBottom: 9,
            fontSize: '0.875rem',
            '&::placeholder': { color: isLight ? alpha('#5B6578', 0.72) : alpha('#9AA6B8', 0.7), opacity: 1 },
          },
          multiline: { paddingTop: 8, paddingBottom: 8 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${isLight ? '#ffffff' : '#121A2B'} inset`,
              WebkitTextFillColor: ink,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 2,
            marginTop: 5,
            fontWeight: 500,
            lineHeight: 1.35,
            '&.Mui-error': {
              color: '#dc2626',
              fontWeight: 600,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 0,
            '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.875rem' },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 44,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 2,
              '&.Mui-checked': { transform: 'translateX(18px)' },
            },
            '& .MuiSwitch-thumb': { width: 22, height: 22 },
            '& .MuiSwitch-track': { borderRadius: 13, opacity: 1 },
          },
        },
      },
      MuiCheckbox: { styleOverrides: { root: { borderRadius: 6 } } },
      MuiRadio: {
        styleOverrides: { root: { '& .MuiSvgIcon-root': { fontSize: 20 } } },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            minHeight: '1.2em',
            display: 'flex',
            alignItems: 'center',
            paddingTop: '9px !important',
            paddingBottom: '9px !important',
          },
          icon: { color: isLight ? alpha('#0B1220', 0.45) : alpha('#E8EEF8', 0.55) },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            paddingTop: '2px !important',
            paddingBottom: '2px !important',
            '& .MuiAutocomplete-input': { minWidth: 60 },
          },
          tag: { borderRadius: 8, fontWeight: 600 },
          paper: {
            borderRadius: 14,
            boxShadow: isLight
              ? '0 16px 40px rgba(11,18,32,0.12)'
              : '0 16px 40px rgba(0,0,0,0.42)',
          },
        },
      },
      MuiDialog: {
        defaultProps: { fullScreen: false },
        styleOverrides: {
          paper: {
            borderRadius: 18,
            margin: 16,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
            boxShadow: isLight
              ? '0 28px 64px rgba(11,18,32,0.16)'
              : '0 28px 64px rgba(0,0,0,0.55)',
            border: `1px solid ${hairline}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: displayFont,
            fontWeight: 700,
            letterSpacing: tracking ?? '-0.02em',
            fontSize: '1.125rem',
            padding: '20px 24px 12px',
            borderBottom: `1px solid ${hairline}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: '18px 24px 20px' } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 24px 20px',
            gap: 8,
            borderTop: `1px solid ${hairline}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${hairline}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            marginBottom: 2,
            transition: 'background-color 0.14s ease',
            '&.Mui-selected': {
              backgroundColor: alpha(safePrimary, isLight ? 0.09 : 0.16),
              '&:hover': { backgroundColor: alpha(safePrimary, isLight ? 0.13 : 0.2) },
            },
            '&:hover': { backgroundColor: alpha(safePrimary, 0.06) },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(18px) saturate(1.4)',
            backgroundColor: isLight ? alpha('#FFFFFF', 0.82) : alpha('#121A2B', 0.86),
            boxShadow: 'none',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 0,
            overflow: 'hidden',
            fontFamily,
            '--DataGrid-rowBorderColor': isLight ? alpha('#0B1220', 0.05) : alpha('#E8EEF8', 0.06),
            '& .MuiDataGrid-columnHeaders': {
              borderRadius: 0,
              backgroundColor: isLight ? '#F7F8FB' : alpha('#0B1220', 0.55),
              borderBottom: `1px solid ${hairline}`,
              minHeight: '46px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              paddingLeft: 16,
              paddingRight: 16,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontFamily: displayFont,
              fontWeight: 650,
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: muted,
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 0.12s ease',
              '&:nth-of-type(even)': {
                backgroundColor: isLight ? alpha('#F7F8FB', 0.7) : alpha('#fff', 0.015),
              },
              '&:hover': { backgroundColor: isLight ? alpha(safePrimary, 0.045) : alpha(safePrimary, 0.1) },
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: alpha(safePrimary, isLight ? 0.07 : 0.14),
              '&:hover': { backgroundColor: alpha(safePrimary, isLight ? 0.1 : 0.18) },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${isLight ? alpha('#0B1220', 0.045) : alpha('#E8EEF8', 0.06)}`,
              fontSize: '0.8375rem',
              paddingLeft: 16,
              paddingRight: 16,
              color: ink,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${hairline}`,
              minHeight: 50,
              backgroundColor: isLight ? '#FBFBFD' : alpha('#0B1220', 0.35),
            },
            '& .MuiDataGrid-virtualScroller': { backgroundColor: surface },
            '& .MuiTablePagination-root': { fontSize: '0.8rem' },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: displayFont,
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
            paddingInline: 16,
          },
        },
      },
      MuiTabs: {
        defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true },
        styleOverrides: {
          indicator: {
            height: 2.5,
            borderRadius: '2px 2px 0 0',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            boxShadow: isLight
              ? '0 16px 40px rgba(11,18,32,0.12)'
              : '0 16px 40px rgba(0,0,0,0.42)',
            border: `1px solid ${hairline}`,
            minWidth: 180,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 9,
            margin: '2px 6px',
            padding: '8px 12px',
            fontSize: '0.875rem',
            fontWeight: 500,
            '&:hover': { backgroundColor: alpha(safePrimary, 0.07) },
            '&.Mui-selected': { backgroundColor: alpha(safePrimary, 0.1), fontWeight: 600 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.75rem',
            fontWeight: 600,
            fontFamily: displayFont,
            padding: '6px 12px',
            backgroundColor: isLight ? '#0B1220' : alpha('#F3F6FB', 0.96),
            color: isLight ? '#F8FAFC' : '#0B1220',
            boxShadow: '0 8px 20px rgba(11,18,32,0.18)',
          },
          arrow: {
            color: isLight ? '#0B1220' : alpha('#F3F6FB', 0.96),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: '1px solid',
            fontWeight: 500,
          },
          message: {
            fontWeight: 700,
          },
          standardInfo: {
            borderColor: alpha('#0284c7', 0.22),
            backgroundColor: alpha('#0284c7', isLight ? 0.06 : 0.12),
            color: isLight ? ink : '#e0f2fe',
            '& .MuiAlert-icon': { color: isLight ? '#0284c7' : '#38bdf8' },
            '& .MuiAlert-message': {
              color: isLight ? ink : '#e0f2fe',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          outlinedInfo: {
            borderColor: alpha('#0284c7', isLight ? 0.45 : 0.55),
            backgroundColor: alpha('#0284c7', isLight ? 0.04 : 0.1),
            color: isLight ? ink : '#e0f2fe',
            '& .MuiAlert-icon': { color: isLight ? '#0284c7' : '#38bdf8' },
            '& .MuiAlert-message': {
              color: isLight ? ink : '#e0f2fe',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          standardSuccess: {
            borderColor: alpha('#059669', 0.22),
            backgroundColor: alpha('#059669', isLight ? 0.06 : 0.12),
            color: isLight ? '#064e3b' : '#d1fae5',
            '& .MuiAlert-icon': { color: isLight ? '#059669' : '#34d399' },
            '& .MuiAlert-message': {
              color: isLight ? '#064e3b' : '#d1fae5',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          outlinedSuccess: {
            borderColor: alpha('#059669', isLight ? 0.45 : 0.55),
            backgroundColor: alpha('#059669', isLight ? 0.05 : 0.1),
            color: isLight ? ink : '#d1fae5',
            '& .MuiAlert-icon': { color: isLight ? '#059669' : '#34d399' },
            '& .MuiAlert-message': {
              color: isLight ? ink : '#d1fae5',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          standardWarning: {
            borderColor: alpha('#d97706', 0.22),
            backgroundColor: alpha('#d97706', isLight ? 0.06 : 0.12),
            color: isLight ? '#78350f' : '#fde68a',
            '& .MuiAlert-icon': { color: isLight ? '#d97706' : '#fbbf24' },
            '& .MuiAlert-message': {
              color: isLight ? '#78350f' : '#fde68a',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          outlinedWarning: {
            borderColor: alpha('#d97706', isLight ? 0.45 : 0.55),
            backgroundColor: alpha('#d97706', isLight ? 0.04 : 0.1),
            color: isLight ? '#78350f' : '#fde68a',
            '& .MuiAlert-icon': { color: isLight ? '#d97706' : '#fbbf24' },
            '& .MuiAlert-message': {
              color: isLight ? '#78350f' : '#fde68a',
              fontWeight: 600,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 600,
            },
          },
          standardError: {
            borderColor: alpha('#dc2626', 0.22),
            backgroundColor: alpha('#dc2626', isLight ? 0.06 : 0.16),
            color: isLight ? '#7f1d1d' : '#fecaca',
            fontWeight: 700,
            '& .MuiAlert-icon': { color: isLight ? '#dc2626' : '#f87171' },
            '& .MuiAlert-message': {
              color: isLight ? '#7f1d1d' : '#fecaca',
              fontWeight: 700,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 700,
            },
          },
          outlinedError: {
            borderColor: alpha('#dc2626', isLight ? 0.45 : 0.55),
            color: isLight ? '#7f1d1d' : '#fecaca',
            fontWeight: 700,
            '& .MuiAlert-icon': { color: isLight ? '#dc2626' : '#f87171' },
            '& .MuiAlert-message': {
              color: isLight ? '#7f1d1d' : '#fecaca',
              fontWeight: 700,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 700,
            },
          },
          filledError: {
            backgroundColor: '#dc2626',
            color: '#ffffff',
            fontWeight: 700,
            '& .MuiAlert-icon': { color: '#ffffff' },
            '& .MuiAlert-message': {
              color: '#ffffff',
              fontWeight: 700,
            },
            '& .MuiAlert-message .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 700,
            },
          },
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            color: 'inherit',
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 700,
            fontSize: '0.65rem',
            minWidth: 18,
            height: 18,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, overflow: 'hidden' },
          bar: { borderRadius: 4 },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isLight ? alpha('#0B1220', 0.06) : alpha('#E8EEF8', 0.08),
            fontSize: '0.8375rem',
            padding: '12px 16px',
          },
          head: {
            fontFamily: displayFont,
            fontWeight: 650,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: muted,
            backgroundColor: isLight ? '#F7F8FB' : alpha('#0B1220', 0.5),
            padding: '11px 16px',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${hairline}`,
            boxShadow: 'none',
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: { fontWeight: 600, fontFamily: displayFont },
          labelContainer: { '& .MuiStepLabel-label.Mui-active': { fontWeight: 700 } },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: '14px !important',
            border: `1px solid ${hairline}`,
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },
    },
  };

  return createTheme({
    ...options,
    components: options.components as ThemeOptions['components'],
  });
}
